import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import { CommunityTemplateDetailResponse } from '@/shared/api/transcriptor/templatesApi';
import { useMedia } from '@/shared/hooks';
import { getLanguageDisplayName } from '@/shared/lib';
import { Icon, Modal, Spinner } from '@/shared/ui';

import { getDocumentTypeLabel, getSpecialtyLabel } from '@/entities/templates';

import styles from './CommunityAssetPreviewDialog.module.scss';

type CommunityAssetPreviewDialogProps = {
  asset: RegistryAsset | null;
  isLoading: boolean;
  isOpen: boolean;
  isPending: boolean;
  detail?: CommunityTemplateDetailResponse | null;
  error?: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

export const CommunityAssetPreviewDialog: FC<
  CommunityAssetPreviewDialogProps
> = (props) => {
  const {
    asset,
    detail,
    error,
    isLoading,
    isOpen,
    isPending,
    onClose,
    onConfirm,
  } = props;
  const { formatMessage, formatNumber, locale } = useIntl();
  const { isMobile } = useMedia();

  if (!asset) {
    return null;
  }

  const isOfficial = asset.originKind === 'official';
  const languageLabel = getLanguageDisplayName(asset.language, locale);
  const typeLabel = asset.templateType
    ? getDocumentTypeLabel(formatMessage, asset.templateType)
    : formatMessage({
        id: 'global.document',
        defaultMessage: 'Document',
      });

  const description =
    detail?.description ??
    formatMessage({
      id: 'communityCatalog.cardNoDescription',
      defaultMessage: 'No description available',
    });

  const sections = detail?.sections ?? [];

  const okButtonText = asset.hasLocalClone
    ? formatMessage({
        id: 'communityCatalog.previewOpenLocalCopy',
        defaultMessage: 'Open local copy',
      })
    : formatMessage({
        id: 'communityCatalog.previewAddToOrganization',
        defaultMessage: 'Add to organization',
      });

  return (
    <Modal
      data-component="community-asset-preview-dialog"
      isOpen={isOpen}
      onCancel={onClose}
      onOk={onConfirm}
      containerClassName={styles.container}
      headerClassName={styles.modalHeader}
      bodyClassName={styles.body}
      headerContent={
        <div className={styles.header}>
          <div className={styles.badges}>
            <span
              className={cn(
                'p4',
                styles.badge,
                isOfficial ? styles.badgeOfficial : styles.badgeCommunity,
              )}
            >
              {isOfficial
                ? formatMessage({
                    id: 'communityCatalog.cardBadgeOfficial',
                    defaultMessage: 'Official',
                  })
                : formatMessage({
                    id: 'communityCatalog.cardBadgeCommunity',
                    defaultMessage: 'Community',
                  })}
            </span>
            {languageLabel && (
              <span className={cn('p4', styles.badge, styles.badgeLanguage)}>
                {languageLabel}
              </span>
            )}
          </div>
          <h3 className={cn(isMobile ? 'h2m' : 'h3', styles.title)}>
            {asset.name}
          </h3>
        </div>
      }
      okButtonText={okButtonText}
      okButtonProps={{
        loading: isPending,
        disabled: isPending || (!asset.hasLocalClone && isLoading),
        size: 'medium',
      }}
      cancelButtonProps={{ disabled: isPending, size: 'medium' }}
    >
      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.stateBlock}>
            <Spinner />
          </div>
        ) : error ? (
          <div className={cn(styles.stateBlock, styles.stateBlockError)}>
            <span className={cn('p3')}>
              {formatMessage({
                id: 'communityCatalog.previewLoadError',
                defaultMessage: 'Failed to load preview',
              })}
            </span>
          </div>
        ) : (
          <>
            <p className={cn('p3', styles.description)}>{description}</p>

            {detail && (
              <dl className={styles.metaList}>
                <div className={styles.metaRow}>
                  <dt className={cn('p4', styles.metaLabel)}>
                    {formatMessage({
                      id: 'communityCatalog.previewMetaType',
                      defaultMessage: 'Type',
                    })}
                  </dt>
                  <dd className={cn('p3', styles.metaValue)}>{typeLabel}</dd>
                </div>
                {!isOfficial && (
                  <>
                    <div className={styles.metaRow}>
                      <dt className={cn('p4', styles.metaLabel)}>
                        {formatMessage({
                          id: 'communityCatalog.previewMetaAuthor',
                          defaultMessage: 'Author',
                        })}
                      </dt>
                      <dd className={cn('p3', styles.metaValue)}>
                        {detail.author_display_name}
                      </dd>
                    </div>
                    <div className={styles.metaRow}>
                      <dt className={cn('p4', styles.metaLabel)}>
                        {formatMessage({
                          id: 'communityCatalog.previewMetaSpecialty',
                          defaultMessage: 'Specialty',
                        })}
                      </dt>
                      <dd className={cn('p3', styles.metaValue)}>
                        {getSpecialtyLabel(
                          formatMessage,
                          detail.author_specialty || detail.specialty,
                        )}
                      </dd>
                    </div>
                  </>
                )}
                <div className={styles.metaRow}>
                  <dt className={cn('p4', styles.metaLabel)}>
                    {formatMessage({
                      id: 'communityCatalog.previewSectionsTitle',
                      defaultMessage: 'Sections',
                    })}
                  </dt>
                  <dd className={cn('p3', styles.metaValue)}>
                    {detail.sections_count}
                  </dd>
                </div>
                {!isOfficial && (
                  <div className={styles.metaRow}>
                    <dt className={cn('p4', styles.metaLabel)}>
                      {formatMessage({
                        id: 'communityCatalog.previewMetaImports',
                        defaultMessage: 'Imports',
                      })}
                    </dt>
                    <dd
                      className={cn(
                        'p3',
                        styles.metaValue,
                        styles.metaValueWithIcon,
                      )}
                    >
                      <Icon name="download" size={16} />
                      {formatNumber(detail.import_count)}
                    </dd>
                  </div>
                )}
              </dl>
            )}

            {detail && detail.tags.length > 0 && (
              <div className={styles.tags}>
                {detail.tags.map((tag) => (
                  <span key={tag} className={cn('p4', styles.badge)}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {sections.length > 0 && (
              <>
                <div className={styles.divider} />

                <div className={styles.sectionsBlock}>
                  <span className={cn('p4', styles.sectionsTitle)}>
                    {formatMessage({
                      id: 'communityCatalog.previewSectionsTitle',
                      defaultMessage: 'Sections',
                    })}
                  </span>
                  <div className={styles.sectionsList}>
                    {sections.map((section, index) => (
                      <div
                        key={section.ID || section.Code || index}
                        className={styles.sectionItem}
                      >
                        <span className={cn('p3', styles.sectionTitle)}>
                          {section.Title}
                        </span>
                        <span className={cn('p4', styles.sectionIndex)}>
                          {index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
